export type PushSubscriptionRecord = {
  subscription_id: string;
  status: string;
  user_agent: string | null;
  expiration_time: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export type PushSubscriptionRegistration = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  expirationTime?: number;
};

type SubscriptionResponse = {
  subscription?: Partial<PushSubscriptionRecord>;
};

type SubscriptionListResponse = {
  subscriptions?: Array<Partial<PushSubscriptionRecord>>;
};

export type TestNotificationResult = {
  requested: number;
  sent: number;
  removed: number;
  failed: number;
};

type TestNotificationResponse = {
  result?: Partial<TestNotificationResult>;
};

// Aggregate counts only -- never subscription IDs, endpoints, keys, or provider response details,
// which notification-service never returns from this endpoint in the first place.
export function describeTestNotificationResult(result: TestNotificationResult): string {
  const { requested, sent, removed, failed } = result;

  if (requested <= 0) {
    return "No registered devices were found to test. Enable notifications on a device, then try again.";
  }

  const deviceWord = (count: number) => (count === 1 ? "device" : "devices");
  // Devices notification-service just removed (expired/unregistered) are no longer real targets,
  // so they don't count against how many of the *remaining* devices were actually reached.
  const effectiveRequested = Math.max(requested - removed, 0);

  const base =
    sent >= effectiveRequested
      ? `Test notification sent to ${sent} registered ${deviceWord(sent)}.`
      : `Test notification sent to ${sent} of ${effectiveRequested} registered ${deviceWord(effectiveRequested)}.`;

  const notes: string[] = [];
  if (failed > 0) {
    notes.push(`${failed} ${failed === 1 ? "delivery failed" : "deliveries failed"}.`);
  }
  if (removed > 0) {
    notes.push(`${removed} expired device ${removed === 1 ? "registration was" : "registrations were"} removed.`);
  }

  return notes.length > 0 ? `${base} ${notes.join(" ")}` : base;
}

// --- DEV-only synthetic reminder validation (see notification-service's
// POST/GET /v1/notifications/synthetic-reminders{/reminderId}, both DEV-only routes) --------

// Fixed and deliberately not caller-configurable: this diagnostic exists to validate the
// deployed reminder engine end to end, not to let a user schedule an arbitrary reminder.
const SYNTHETIC_REMINDER_DELAY_MINUTES = 2;

// Mirrors notification-service worker.py's TERMINAL_STATUSES plus the in-flight "claimed"
// status -- "scheduled" is this client's own label for "no occurrence yet", which the backend
// represents as occurrence: null (the worker has not reached this reminder's due bucket yet).
export type SyntheticReminderState = "scheduled" | "processing" | "provider-accepted" | "suppressed" | "failed";

export type ScheduledSyntheticReminder = {
  reminderId: string;
  dueAt: string | null;
};

export type SyntheticReminderStatusResult = {
  dueAt: string | null;
  state: SyntheticReminderState;
};

type ScheduleSyntheticReminderResponse = {
  reminder?: { reminder_id?: string; next_due_at?: string | null };
};

type SyntheticReminderStatusResponse = {
  reminder?: {
    next_due_at?: string | null;
    occurrence?: { status?: string } | null;
  };
};

function normalizeSyntheticReminderState(occurrenceStatus: string | null | undefined): SyntheticReminderState {
  switch (occurrenceStatus) {
    case "claimed":
      return "processing";
    case "provider-accepted":
      return "provider-accepted";
    case "suppressed":
      return "suppressed";
    case "failed":
      return "failed";
    default:
      return "scheduled";
  }
}

export function isTerminalSyntheticReminderState(state: SyntheticReminderState): boolean {
  return state === "provider-accepted" || state === "suppressed" || state === "failed";
}

export type SyntheticReminderStateCopy = { label: string; description: string };

// Truthful, non-alarming copy for every state -- "Provider accepted" (never "Delivered") is
// explicit that push-provider acceptance is not confirmation the device actually received it,
// and "Suppressed" reads as an expected diagnostic outcome (e.g. Care Reminders is off), not a
// failure.
export function describeSyntheticReminderState(state: SyntheticReminderState): SyntheticReminderStateCopy {
  switch (state) {
    case "scheduled":
      return { label: "Scheduled", description: "Waiting for the reminder engine to pick this up." };
    case "processing":
      return { label: "Processing", description: "The reminder engine is processing this test reminder now." };
    case "provider-accepted":
      return {
        label: "Provider accepted",
        description: "MedoPilot's push provider accepted this reminder for delivery. This does not confirm your device received it.",
      };
    case "suppressed":
      return {
        label: "Suppressed",
        description: "This reminder was not sent. If Care reminders is off, that's expected.",
      };
    case "failed":
      return { label: "Failed", description: "The reminder engine could not process this test reminder." };
  }
}

// A short, user-friendly clock time in the browser's own locale/timezone -- next_due_at itself
// is always UTC (see notification-service's SYNTHETIC_REMINDER_DEFAULT_TIMEZONE), never shown
// as-is.
export function formatSyntheticReminderDueTime(dueAt: string | null): string | null {
  if (!dueAt) {
    return null;
  }
  const date = new Date(dueAt);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
}

export class NotificationServiceError extends Error {
  constructor(message = "We could not update your notification settings right now.") {
    super(message);
    this.name = "NotificationServiceError";
  }
}

function textOrNull(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function normalizeSubscription(record: Partial<PushSubscriptionRecord> | undefined): PushSubscriptionRecord {
  return {
    subscription_id: textOrNull(record?.subscription_id) ?? "",
    status: textOrNull(record?.status) ?? "active",
    user_agent: textOrNull(record?.user_agent),
    expiration_time: typeof record?.expiration_time === "number" ? record.expiration_time : null,
    created_at: textOrNull(record?.created_at),
    updated_at: textOrNull(record?.updated_at),
  };
}

async function requestNotification<TBody>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  path: string,
  method: "GET" | "POST" | "DELETE",
  body?: TBody,
  signal?: AbortSignal,
) {
  if (!accessToken) {
    throw new NotificationServiceError("Please sign in again to manage your notification settings.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 400) {
      throw new NotificationServiceError("This device's push subscription could not be registered.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new NotificationServiceError("Please sign in again to manage your notification settings.");
    }

    throw new NotificationServiceError();
  }

  if (response.status === 204) {
    return {} as unknown;
  }

  return (await response.json()) as unknown;
}

export function createNotificationServiceClient(apiBaseUrl: string, getAccessToken: () => string | undefined) {
  return {
    async registerSubscription(subscription: PushSubscriptionRegistration, signal?: AbortSignal) {
      const data = (await requestNotification(
        apiBaseUrl,
        getAccessToken(),
        "/v1/notifications/subscriptions",
        "POST",
        subscription,
        signal,
      )) as SubscriptionResponse;
      return normalizeSubscription(data.subscription);
    },
    async listSubscriptions(signal?: AbortSignal) {
      const data = (await requestNotification(
        apiBaseUrl,
        getAccessToken(),
        "/v1/notifications/subscriptions",
        "GET",
        undefined,
        signal,
      )) as SubscriptionListResponse;
      return (data.subscriptions ?? []).map(normalizeSubscription).filter((subscription) => subscription.subscription_id);
    },
    async deleteSubscription(subscriptionId: string, signal?: AbortSignal) {
      await requestNotification(
        apiBaseUrl,
        getAccessToken(),
        `/v1/notifications/subscriptions/${encodeURIComponent(subscriptionId)}`,
        "DELETE",
        undefined,
        signal,
      );
    },
    async sendTestNotification(signal?: AbortSignal): Promise<TestNotificationResult> {
      const data = (await requestNotification(
        apiBaseUrl,
        getAccessToken(),
        "/v1/notifications/test",
        "POST",
        undefined,
        signal,
      )) as TestNotificationResponse;
      return {
        requested: data.result?.requested ?? 0,
        sent: data.result?.sent ?? 0,
        removed: data.result?.removed ?? 0,
        failed: data.result?.failed ?? 0,
      };
    },
    async scheduleSyntheticReminder(signal?: AbortSignal): Promise<ScheduledSyntheticReminder> {
      const data = (await requestNotification(
        apiBaseUrl,
        getAccessToken(),
        "/v1/notifications/synthetic-reminders",
        "POST",
        { delay_minutes: SYNTHETIC_REMINDER_DELAY_MINUTES },
        signal,
      )) as ScheduleSyntheticReminderResponse;

      const reminderId = textOrNull(data.reminder?.reminder_id);
      if (!reminderId) {
        throw new NotificationServiceError("We could not schedule a test reminder right now.");
      }

      return { reminderId, dueAt: textOrNull(data.reminder?.next_due_at ?? null) };
    },
    async getSyntheticReminderStatus(reminderId: string, signal?: AbortSignal): Promise<SyntheticReminderStatusResult> {
      const data = (await requestNotification(
        apiBaseUrl,
        getAccessToken(),
        `/v1/notifications/synthetic-reminders/${encodeURIComponent(reminderId)}`,
        "GET",
        undefined,
        signal,
      )) as SyntheticReminderStatusResponse;

      return {
        dueAt: textOrNull(data.reminder?.next_due_at ?? null),
        state: normalizeSyntheticReminderState(data.reminder?.occurrence?.status),
      };
    },
  };
}
